import type { EditorSnapshot, TextOverlay } from "./types";

const DATABASE_NAME = "lubosh-pdf-lite";
const DATABASE_VERSION = 1;
const STORE_NAME = "workspace";
const PDF_KEY = "current-pdf";
const STATE_KEY = "current-state";

interface StoredPdf {
  version: 1;
  name: string;
  bytes: ArrayBuffer;
}

export interface StoredEditorState {
  version: 1;
  fontAnalysisVersion?: number;
  snapshot: EditorSnapshot;
  activePage: number;
  zoom: number;
  selectedEditId: string | null;
  rightTab: "edit" | "fonts" | "ocr";
  leftTab: "pages" | "layers";
  ocrByPage: Record<number, TextOverlay[]>;
  ocrLanguages: string[];
  autoOcrEnabled: boolean;
}

export interface StoredWorkspace {
  pdf: StoredPdf;
  state: StoredEditorState | null;
}

const requestResult = <T>(request: IDBRequest<T>) => new Promise<T>((resolve, reject) => {
  request.addEventListener("success", () => resolve(request.result), { once: true });
  request.addEventListener("error", () => reject(request.error), { once: true });
});

const transactionDone = (transaction: IDBTransaction) => new Promise<void>((resolve, reject) => {
  transaction.addEventListener("complete", () => resolve(), { once: true });
  transaction.addEventListener("abort", () => reject(transaction.error), { once: true });
  transaction.addEventListener("error", () => reject(transaction.error), { once: true });
});

const openDatabase = async () => {
  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
  request.addEventListener("upgradeneeded", () => {
    if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME);
  }, { once: true });
  return requestResult(request);
};

export const loadStoredWorkspace = async (): Promise<StoredWorkspace | null> => {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const [pdf, state] = await Promise.all([
      requestResult(store.get(PDF_KEY)) as Promise<StoredPdf | undefined>,
      requestResult(store.get(STATE_KEY)) as Promise<StoredEditorState | undefined>,
    ]);
    await transactionDone(transaction);
    if (!pdf || pdf.version !== 1 || !(pdf.bytes instanceof ArrayBuffer)) return null;
    return { pdf, state: state?.version === 1 ? state : null };
  } finally {
    database.close();
  }
};

export const saveStoredPdf = async (bytes: Uint8Array, name: string) => {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put({ version: 1, name, bytes: bytes.slice().buffer } satisfies StoredPdf, PDF_KEY);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
};

export const saveStoredEditorState = async (state: StoredEditorState) => {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(state, STATE_KEY);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
};

export const clearStoredWorkspace = async () => {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).clear();
    await transactionDone(transaction);
  } finally {
    database.close();
  }
};
